import { useCallback, useContext, useId, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { omitKeys } from 'shared/utils/types/omitKeys'
import { SELECTED_OBJECTS_PARAM } from 'site/Renderer/components/DataTables/hooks/useSelectedRowsFromSearchParams'
import { SelectAndLinkContext } from 'site/Renderer/components/SelectAndLink'
import { targetComponents } from 'site/Renderer/components/SelectAndLink/SelectAndLink.constants'
import { useSiteState } from 'siteState/shared'
import useCancelModal, { Callbacks } from './useCancelModal'

function removeParam(prev: URLSearchParams, key: string): URLSearchParams {
  const prevEntries = Array.from(prev.entries())
  return new URLSearchParams(prevEntries.filter(([k]) => k !== key))
}

export default function useCancelChanges() {
  const selectAndLinkContext = useContext(SelectAndLinkContext)
  const [, setSearchParams] = useSearchParams()
  const { open } = useCancelModal()
  const [_, setEditCallbacks] = useSiteState<Record<string, Callbacks>>(
    'cancel-modal-callbacks'
  )
  const id = useId()

  const hasObjectViewSelection = useMemo(() => {
    return (
      selectAndLinkContext &&
      Object.values(selectAndLinkContext.sessions).some(
        (session) => session.targetComponent === targetComponents.OBJECT_VIEW
      )
    )
  }, [selectAndLinkContext])

  const clearObjectViewSelectionSessionsOnly = useCallback(() => {
    if (!selectAndLinkContext) return

    Object.keys(selectAndLinkContext.sessions)
      .filter(
        (sessionId) =>
          selectAndLinkContext.sessions[sessionId]?.targetComponent ===
          targetComponents.OBJECT_VIEW
      )
      .forEach((sessionId) => selectAndLinkContext.onClearSelection(sessionId))
  }, [selectAndLinkContext])

  const discardObjectViewSelection = useCallback(() => {
    clearObjectViewSelectionSessionsOnly()
    setSearchParams((prev) => removeParam(prev, SELECTED_OBJECTS_PARAM), {
      replace: true
    })
  }, [clearObjectViewSelectionSessionsOnly, setSearchParams])

  const removeCallbackById = useCallback(
    (targetId: string) => setEditCallbacks((prev) => omitKeys(prev, targetId)),
    [setEditCallbacks]
  )

  const confirmIfPendingChanges = useCallback(
    (fn: () => void, onSuccess?: () => void, onCancel?: () => void) => {
      if (!selectAndLinkContext || !hasObjectViewSelection) {
        fn()
        return
      }

      setEditCallbacks((prev) => ({
        ...prev,
        [id]: {
          onSucceed: () => {
            removeCallbackById(id)
            if (onSuccess !== undefined) {
              clearObjectViewSelectionSessionsOnly()
              queueMicrotask(() => onSuccess())
            } else {
              discardObjectViewSelection()
            }
          },
          onCancel: () => {
            removeCallbackById(id)
            onCancel?.()
          }
        }
      }))

      open(id)
    },
    [
      clearObjectViewSelectionSessionsOnly,
      discardObjectViewSelection,
      hasObjectViewSelection,
      id,
      open,
      removeCallbackById,
      selectAndLinkContext,
      setEditCallbacks
    ]
  )

  return { confirmIfPendingChanges }
}
